import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { partnerApiService } from '../../services/api';
import { MenuCategory, MenuItem } from '../../types/api';
import { MenuItemForm } from '../MenuItemForm';
import { categoryPathLabel, isLeafCategory } from '../../utils/categoryTree';

export const CategoryItemsPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<MenuCategory | null>(null);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [categories, setCategories] = useState<MenuCategory[]>([]);

  const loadCategoryAndItems = useCallback(async () => {
    if (!categoryId) return;

    try {
      setLoading(true);
      setError(null);
      const parsedId = parseInt(categoryId, 10);

      // Загружаем список категорий и позиции для текущей категории
      // Используем getMenuItems вместо getMenuCategory, так как последний вызывает 403 ошибку
      const [categoriesData, itemsData] = await Promise.all([
        partnerApiService.getMenuCategories(),
        partnerApiService.getMenuItems(parsedId)
      ]);

      const currentCategory = categoriesData.find(c => c.id === parsedId);

      if (!currentCategory) {
        throw new Error('Категория не найдена в списке ваших категорий');
      }

      setCategory(currentCategory);
      setCategories(categoriesData);
      setItems(itemsData);
    } catch (err) {
      console.error('Ошибка загрузки категории:', err);
      setError('Не удалось загрузить информацию о категории');
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    if (categoryId) {
      loadCategoryAndItems();
    }
  }, [categoryId, loadCategoryAndItems]);

  const handleCreateItem = () => {
    setShowCreateForm(true);
  };

  const handleEditItem = (item: MenuItem) => {
    setEditingItem(item);
    setShowEditForm(true);
  };

  const handleFormSave = () => {
    setShowCreateForm(false);
    setShowEditForm(false);
    setEditingItem(null);
    loadCategoryAndItems(); // Перезагружаем данные
  };

  const handleFormCancel = () => {
    setShowCreateForm(false);
    setShowEditForm(false);
    setEditingItem(null);
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту позицию?')) {
      return;
    }

    try {
      await partnerApiService.deleteMenuItem(itemId);
      setItems(prev => prev.filter(item => item.id !== itemId));
    } catch (err) {
      console.error('Ошибка удаления позиции:', err);
      setError('Не удалось удалить позицию меню');
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'currency',
      currency: 'KZT',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Показываем форму создания или редактирования
  if (showCreateForm || showEditForm) {
    return (
      <MenuItemForm
        item={editingItem || undefined}
        categories={categories}
        initialCategoryId={category ? category.id : undefined}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
        <button
          onClick={() => navigate('/catalog')}
          className="mt-4 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
        >
          Вернуться к каталогу
        </button>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-8 text-gray-500">
          Категория не найдена
        </div>
        <button
          onClick={() => navigate('/catalog')}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
        >
          Вернуться к каталогу
        </button>
      </div>
    );
  }

  const subcategories = categories.filter((candidate) => candidate.parent_id === category.id);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Заголовок и навигация */}
      <div className="mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/catalog')}
            className="text-primary-600 hover:text-primary-800 flex items-center space-x-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Назад к каталогу</span>
          </button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {categoryPathLabel(category, categories)}
        </h1>
        <p className="text-gray-600">
          Управление позициями в разделе
        </p>
      </div>

      {/* Статистика */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-primary-600">Название категории</p>
            <p className="font-semibold text-primary-900">{category.name}</p>
          </div>
          <div>
            <p className="text-sm text-primary-600">Всего позиций</p>
            <p className="font-semibold text-primary-900">{items.length}</p>
          </div>
          <div>
            <p className="text-sm text-primary-600">ID категории</p>
            <p className="font-semibold text-primary-900">{category.id}</p>
          </div>
        </div>
      </div>

      {/* Раздел с подразделами товаров не держит: вместо кнопки — переходы
          в подразделы, иначе партнёр упрётся в ошибку сервера. */}
      {!isLeafCategory(category, categories) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md mb-6">
          <p className="mb-2">
            В этом разделе есть подразделы — товары лежат в них.
          </p>
          <div className="flex flex-wrap gap-2">
            {subcategories.map((child) => (
              <button
                key={child.id}
                onClick={() => navigate(`/catalog/category/${child.id}`)}
                className="bg-white border border-amber-300 rounded-md px-3 py-1 hover:bg-amber-100"
              >
                {child.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Кнопка добавления */}
      <div className="mb-6" hidden={!isLeafCategory(category, categories)}>
        <button
          onClick={handleCreateItem}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Добавить позицию</span>
        </button>
      </div>

      {/* Список позиций */}
      <div className="space-y-4">
        {!items || items.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Позиции не найдены</h3>
            <p className="text-gray-500 mb-4">
              В этой категории пока нет позиций. Создайте первую позицию!
            </p>
            <button
              onClick={handleCreateItem}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Добавить позицию
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3 gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 text-lg break-words">{item.name}</h4>
                    <p className="text-xl font-semibold text-green-600">
                      {formatPrice(item.price)}
                    </p>
                  </div>
                  <div className="flex space-x-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditItem(item)}
                      className="text-primary-600 hover:text-primary-800 p-1"
                      title="Редактировать позицию"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Удалить позицию"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {item.images && Array.isArray(item.images) && item.images.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Изображения:</p>
                    <div className="flex space-x-2">
                      {item.images.slice(0, 3).map((image, imgIndex) => (
                        <div key={image.id} className="relative group">
                          <img
                            src={image.url}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-md border"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-md"></div>
                          <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-cover text-xs px-1 py-0.5 rounded">
                            #{imgIndex + 1}
                          </div>
                        </div>
                      ))}
                      {item.images.length > 3 && (
                        <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-500">
                          +{item.images.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-500">
                  ID: {item.id}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 